import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PencilSquareIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { VcaCheckStep } from './VcaCheckStep';
import { VcaNotesStep } from './VcaNotesStep';
import { usePatientCard } from '../../../../../hooks/usePatientCard';
import { VcaReviewStep } from './VcaReviewStep';
import { submitTrigger, editTrigger } from './vca-trigger-api';
import { advanceWorkflow } from '../../../../../services/workQueue';
import { apiErrorMessage } from '../../../../../utils/apiError';
import { formatNumericDate } from '../../../../../utils/dates';
import { ConfirmDiscardModal } from '../../ConfirmDiscardModal';
import { resolveDocImagesInBackground } from '../../../../../utils/docImageResolution';
import { applyResolvedUrlsToDoc, tiptapDocToPlainText } from '@odo/shared-ui';
import { EMPTY_TIPTAP_DOC } from '../../dx-report/templates';
import { tiptapDocFromString } from '../../dx-report/editor/editorState';
import type { ActionRunnerProps, TimelineEvent } from '../../types';
import type { VcaTriggerConfig } from './vca-trigger-config';
import { resolvePatientFirstName, type VcaNoteFields } from './vcaNoteTemplates';
import { checkStepHint, recordsCaseChecklist, toChecklistPayload } from './vca-check-step';
import type { TiptapDoc } from '@odo/shared-ui';

type StepId = 'preflight' | 'notes' | 'review';

interface StepMeta {
    id: StepId;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
}

const STEPS: StepMeta[] = [
    { id: 'preflight', label: 'Check', icon: ShieldCheckIcon },
    { id: 'notes', label: 'Notes', icon: PencilSquareIcon },
    { id: 'review', label: 'Review', icon: CheckCircleIcon }
];

// An edit only rewrites the notes: the Check step was done on the original trigger.
const EDIT_STEPS: StepMeta[] = STEPS.filter((step) => step.id !== 'preflight');

// Keep in sync with the backend triggerBody.notes max (trigger.validator.ts).
const NOTES_MAX_LENGTH = 20000;

function isNotesValid(doc: TiptapDoc): boolean {
    const len = tiptapDocToPlainText(doc).trim().length;
    return len > 0 && len <= NOTES_MAX_LENGTH;
}

function extractInitialNotesDoc(event: TimelineEvent): TiptapDoc {
    const payload = event.payload;
    if (!payload || typeof payload !== 'object') return EMPTY_TIPTAP_DOC;
    const raw = payload as Record<string, unknown>;
    if (raw.notesDoc && typeof raw.notesDoc === 'object') return raw.notesDoc as TiptapDoc;
    if (typeof raw.notes === 'string') return tiptapDocFromString(raw.notes);
    return EMPTY_TIPTAP_DOC;
}

function capitalize(word: string): string {
    return word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
}

type VcaTriggerWizardProps = Omit<ActionRunnerProps, 'patientName'> & {
    editingEvent?: TimelineEvent;
    config: VcaTriggerConfig;
    // Fired with the status a NEW trigger moved the case to, so a work-queue surface can complete;
    // edits don't transition, so they don't fire it.
    onSubmitted?: (landedOn: string) => void;
};

function VcaTriggerWizard({
    patientId,
    onBack,
    onDataChange,
    editingEvent,
    config,
    onSubmitted
}: VcaTriggerWizardProps) {
    const isEditMode = !!editingEvent;
    const steps = isEditMode ? EDIT_STEPS : STEPS;
    const [stepIndex, setStepIndex] = useState(0);

    const initialNotesDoc = useMemo<TiptapDoc>(
        () => (editingEvent ? extractInitialNotesDoc(editingEvent) : EMPTY_TIPTAP_DOC),
        [editingEvent]
    );

    const [notesDoc, setNotesDoc] = useState<TiptapDoc>(() => initialNotesDoc);
    const { data: card } = usePatientCard(patientId);
    // New triggers only: an edit reopens notes that were already written.
    const notesPresets = useMemo(() => {
        if (isEditMode) return undefined;
        const fields: VcaNoteFields = {
            doctorLastName: card?.practice?.drLastName ?? null,
            patientFirstName: resolvePatientFirstName(card?.firstName, card?.name),
            currentAligner: card?.monitoring?.currentAligner ?? null,
            datePosted: formatNumericDate(new Date())
        };
        return config.notesPresets.map((preset) => preset(fields));
    }, [isEditMode, config, card]);

    const checkKind = config.checkStep.kind;
    const [checkConfirmed, setCheckConfirmed] = useState(false);
    const checkHint = checkStepHint(checkKind, checkConfirmed);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    useEffect(() => {
        if (!editingEvent) return undefined;
        return resolveDocImagesInBackground(
            patientId,
            [initialNotesDoc],
            'VcaTriggerWizard.resolveNoteImageUrls',
            (urls) => setNotesDoc((prev) => applyResolvedUrlsToDoc(prev, urls))
        );
    }, [editingEvent, patientId, initialNotesDoc]);

    const currentStep = steps[stepIndex];

    const isDirty = useMemo(() => {
        if (JSON.stringify(notesDoc) !== JSON.stringify(initialNotesDoc)) return true;
        if (!isEditMode && checkConfirmed) return true;
        return false;
    }, [notesDoc, initialNotesDoc, isEditMode, checkConfirmed]);

    const handleRequestExit = useCallback(() => {
        if (isDirty) {
            setShowDiscardConfirm(true);
        } else {
            onBack();
        }
    }, [isDirty, onBack]);

    const handleConfirmDiscard = useCallback(() => {
        setShowDiscardConfirm(false);
        onBack();
    }, [onBack]);

    const handleCancelDiscard = useCallback(() => {
        setShowDiscardConfirm(false);
    }, []);

    const stepValidity = useMemo<Record<StepId, boolean>>(
        () => ({
            preflight: checkConfirmed,
            notes: isNotesValid(notesDoc),
            review: true
        }),
        [checkConfirmed, notesDoc]
    );

    const canAdvance = stepValidity[currentStep.id];

    const notesLength = useMemo(() => tiptapDocToPlainText(notesDoc).trim().length, [notesDoc]);

    const handleNext = useCallback(() => {
        setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }, [steps.length]);

    const handleBack = useCallback(() => {
        setStepIndex((i) => Math.max(i - 1, 0));
    }, []);

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const notes = tiptapDocToPlainText(notesDoc);
            if (isEditMode && editingEvent) {
                // Edit only updates the stored notes — the status transition already
                // happened on the initial trigger, so we don't re-transition.
                await editTrigger(config.apiPath, {
                    patientId,
                    eventId: editingEvent.id,
                    notes,
                    notesDoc
                });
            } else {
                // Two calls: 1) store the trigger data, 2) advance the workflow so the engine
                // moves the case from its treatment step (Tx-1 or a VA step) to Ref-1/Ret-1.
                await submitTrigger(config.apiPath, {
                    patientId,
                    notes,
                    notesDoc,
                    checklist: toChecklistPayload(checkKind, checkConfirmed)
                });
                await advanceWorkflow(patientId, config.workflowAction);
            }
            setSubmitted(true);
            onDataChange?.();
            // A NEW trigger moved the case to its next status; let the work-queue
            // surface refresh + return to the patient list. Edits don't transition.
            if (!isEditMode) onSubmitted?.(config.targetInternalCaseStatus);
        } catch (error: unknown) {
            // Prefer the API's own message, e.g. a workflow guard's 409 reason.
            const fallback = error instanceof Error ? error.message : 'Unknown error';
            setSubmitError(`Failed to submit: ${apiErrorMessage(error, fallback)}`);
        } finally {
            setIsSubmitting(false);
        }
    }, [
        patientId,
        notesDoc,
        isEditMode,
        editingEvent,
        onDataChange,
        onSubmitted,
        checkKind,
        checkConfirmed,
        config.apiPath,
        config.workflowAction,
        config.targetInternalCaseStatus
    ]);

    const SingularCapitalized = capitalize(config.singular);
    const backToListLabel = `Back to ${config.plural}`;
    const headerTitle = isEditMode
        ? `Edit ${config.singular} trigger`
        : `New ${config.singular} trigger`;
    const submitLabel = isEditMode ? 'Save changes' : `Trigger ${config.singular}`;
    const submittingLabel = 'Triggering…';
    const successTitle = isEditMode
        ? `${SingularCapitalized} updated`
        : `${SingularCapitalized} triggered`;

    if (submitted) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-500" />
                </div>
                <div className="text-center">
                    <div className="text-sm font-semibold text-gray-800">{successTitle}</div>
                    <div className="text-xs text-gray-500 mt-1">
                        Monday has been updated and the action is recorded in the timeline.
                    </div>
                </div>
                <button
                    onClick={onBack}
                    className="mt-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    {backToListLabel}
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <ConfirmDiscardModal
                isOpen={showDiscardConfirm}
                onCancel={handleCancelDiscard}
                onDiscard={handleConfirmDiscard}
            />

            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={handleRequestExit}
                    className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    {backToListLabel}
                </button>
                <div className="text-sm font-semibold text-gray-800">{headerTitle}</div>
                <div className="w-[120px]" />
            </div>

            <div className="flex items-center justify-center gap-2 px-5 py-4 border-b border-gray-200 flex-shrink-0">
                {steps.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = index === stepIndex;
                    const isPast = index < stepIndex;
                    return (
                        <div key={step.id} className="flex items-center">
                            <div
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                    isActive
                                        ? 'bg-gray-900 text-white'
                                        : isPast
                                          ? 'bg-gray-100 text-gray-700'
                                          : 'bg-gray-50 text-gray-400'
                                }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                <span>{step.label}</span>
                            </div>
                            {index < steps.length - 1 && (
                                <div
                                    className={`w-6 h-px mx-1 ${isPast ? 'bg-gray-300' : 'bg-gray-200'}`}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            <div
                className={`flex-1 min-h-0 ${currentStep.id === 'notes' ? 'flex flex-col overflow-hidden' : 'overflow-auto p-5'}`}
            >
                {currentStep.id === 'preflight' && (
                    <VcaCheckStep
                        config={config.checkStep}
                        confirmed={checkConfirmed}
                        onChange={setCheckConfirmed}
                    />
                )}
                {currentStep.id === 'notes' && (
                    <VcaNotesStep
                        patientId={patientId}
                        value={notesDoc}
                        onChange={setNotesDoc}
                        presets={notesPresets}
                    />
                )}
                {currentStep.id === 'review' && (
                    <VcaReviewStep
                        patientId={patientId}
                        notesDoc={notesDoc}
                        targetCaseStatus={config.targetCaseStatus}
                        targetActionRequired={config.targetActionRequired}
                        targetActionBy={config.targetActionBy}
                        targetInternalCaseStatus={config.targetInternalCaseStatus}
                        notesPanelTitle={config.notesPanelTitle}
                        showCaseChecklist={!isEditMode && recordsCaseChecklist(checkKind)}
                    />
                )}
            </div>

            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 flex-shrink-0">
                <button
                    onClick={handleBack}
                    disabled={stepIndex === 0}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Back
                </button>

                <div className="flex items-center gap-3">
                    {currentStep.id === 'notes' && (
                        <span
                            className={`text-xs tabular-nums ${notesLength > NOTES_MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}
                        >
                            {notesLength.toLocaleString()} / {NOTES_MAX_LENGTH.toLocaleString()}
                        </span>
                    )}
                    {currentStep.id === 'preflight' && checkHint && (
                        <span className="text-xs text-gray-500">{checkHint}</span>
                    )}
                    {submitError && <span className="text-xs text-red-500">{submitError}</span>}
                    {currentStep.id === 'review' ? (
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting && (
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            )}
                            {isSubmitting ? submittingLabel : submitLabel}
                        </button>
                    ) : (
                        <button
                            onClick={handleNext}
                            disabled={!canAdvance}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Next
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export { VcaTriggerWizard };
